import { useCallback, useEffect, useMemo, useState } from "react";
import { listNewsletterEmails, loadNewsletterDocument } from "../../../../services/newsletterStorageService";
import { formatStorageDisplayName } from "../../newsletter-editor/utils/storageDisplayUtils";
import { compileDocumentToHtml } from "../../newsletter-editor/services/htmlCompiler";
import { useNewsletterPublishFeedback } from "./useNewsletterPublishFeedback";

export const useNewsletterPublishState = () => {
  const notify = useNewsletterPublishFeedback();
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState("");
  const [subject, setSubject] = useState("");
  const [compiledHtml, setCompiledHtml] = useState("");
  const [compileErrors, setCompileErrors] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [subjectWarning, setSubjectWarning] = useState(false);

  useEffect(() => {
    listNewsletterEmails()
      .then(setTemplates)
      .catch((err) => notify({ title: "Errore template", message: err.message }))
      .finally(() => setTemplatesLoading(false));
  }, [notify]);

  const loadTemplate = useCallback(
    async (objectKey) => {
      if (!objectKey) {
        setSubject("");
        setCompiledHtml("");
        setCompileErrors([]);
        setSubjectWarning(false);
        return;
      }
      setTemplateLoading(true);
      try {
        const document = await loadNewsletterDocument(objectKey);
        const resolvedSubject = (document.subject || "").trim();
        setSubject(resolvedSubject);
        setSubjectWarning(!resolvedSubject);
        const { html, errors } = compileDocumentToHtml(document);
        setCompiledHtml(html || "");
        setCompileErrors(errors || []);
        if (errors?.length) {
          notify({
            title: "Errori compilazione template",
            message: "Correggi il template nell'editor prima di inviare.",
            detail: errors.join(" "),
          });
        }
      } catch (err) {
        notify({ title: "Caricamento template non riuscito", message: err.message });
      } finally {
        setTemplateLoading(false);
      }
    },
    [notify]
  );

  useEffect(() => {
    loadTemplate(selectedTemplateKey);
  }, [selectedTemplateKey, loadTemplate]);

  const templateOptions = useMemo(
    () =>
      templates.map((item) => ({
        key: item.objectKey,
        label: formatStorageDisplayName(item, "email"),
      })),
    [templates]
  );

  const canPrepare =
    selectedTemplateKey &&
    subject.trim() &&
    compiledHtml.trim() &&
    compileErrors.length === 0 &&
    !templateLoading;

  const handleSubjectChange = (value) => {
    setSubject(value);
    setSubjectWarning(!value.trim());
  };

  return {
    templatesLoading,
    templateLoading,
    selectedTemplateKey,
    setSelectedTemplateKey,
    subject,
    handleSubjectChange,
    compiledHtml,
    compileErrors,
    subjectWarning,
    templateOptions,
    canPrepare,
  };
};
